import type { ChartComponentSpec, ChartWidgetMutations, ChartSeriesPlotMutations, DataPoint } from "./types";
import { Y_AXIS_INDEX } from "./presets";
import { DEFAULT_REFERENCE_BAND_COLOR } from "./referenceBands";
import { formatToDecimals, inferDecimals, roundToDecimals } from "./decimals";

function formatTrendlineName(seriesName: string): string {
  const match = seriesName.match(/^(\[[^\]]+\])\s*(.*)$/);
  if (match) return `${match[1]} (Trendline) ${match[2]}`;
  return `(Trendline) ${seriesName}`;
}

type MutationContext = {
  queryId?: string;
  yAxis?: number;
  spec: ChartComponentSpec;
};

interface WidgetMutation {
  scope: "widget";
  test: (wm: ChartWidgetMutations) => boolean;
  apply: (config: Record<string, unknown>, ctx: MutationContext) => Record<string, unknown>;
}

interface SeriesMutation {
  scope: "series";
  test: (pm: ChartSeriesPlotMutations) => boolean;
  apply: (config: Record<string, unknown>, ctx: MutationContext) => Record<string, unknown>;
}

type Mutation = WidgetMutation | SeriesMutation;

function linearRegression(data: DataPoint[]): { slope: number; intercept: number } {
  const points: { x: number; y: number }[] = [];
  data.forEach((d, i) => {
    if (d == null) return;
    if (typeof d !== "number" && d.custom?.injured === true) return;
    const y = typeof d === "number" ? d : d.y;
    points.push({ x: i, y });
  });
  if (points.length < 2) return { slope: 0, intercept: 0 };
  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

type BandLabelPlacement = "right" | "top" | "inside";

/**
 * Band labels stay off the chart for now: the label is a name for the band in
 * the configurator list only. Flip this to draw each label beside its band
 * (outside the plot area) and reserve the margin for it.
 */
const SHOW_REFERENCE_BAND_LABELS = false;

const BAND_LABEL_FONT_PX = 9;

function bandLabelPlacement(inverted: boolean, dualAxis: boolean): BandLabelPlacement {
  if (dualAxis) return "inside";
  return inverted ? "top" : "right";
}

function escapeLabelText(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** A readable mid-tone of a pastel band colour, for the dash that ties the label to its band. */
function bandDashColor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#888";
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  let s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  s = d === 0 ? 0 : Math.max(s, 0.55);
  return `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(Math.min(l, 0.45) * 100)}%)`;
}

function bandLabelOptions(
  label: string,
  color: string,
  placement: BandLabelPlacement,
): Record<string, unknown> {
  const text = `<span style="color:${bandDashColor(color)}">▬</span> ${escapeLabelText(label)}`;
  const style = { color: "#666", fontSize: `${BAND_LABEL_FONT_PX}px`, whiteSpace: "nowrap" };
  switch (placement) {
    case "right":
      return { text, align: "right", textAlign: "left", verticalAlign: "middle", x: 8, style };
    case "top":
      return { text, align: "center", textAlign: "center", verticalAlign: "top", y: -14, style };
    default:
      return { text, align: "right", textAlign: "right", verticalAlign: "top", x: -6, y: 12, style };
  }
}

/** Grow the chart margin that the outside band labels hang into. */
function reserveBandLabelSpace(
  config: Record<string, unknown>,
  placement: BandLabelPlacement,
  labels: string[],
): Record<string, unknown> {
  if (labels.length === 0 || placement === "inside") return config;
  const chart = { ...((config.chart as Record<string, unknown>) ?? {}) };
  if (placement === "right") {
    const longest = Math.max(...labels.map((l) => l.length));
    const needed = 8 + 14 + Math.ceil(longest * BAND_LABEL_FONT_PX * 0.62) + 8;
    const prev = (chart.marginRight as number | undefined) ?? 0;
    chart.marginRight = Math.max(prev, needed);
    return { ...config, chart };
  }
  const legend = { ...((config.legend as Record<string, unknown>) ?? {}) };
  const gap = 30;
  if (legend.enabled === false) {
    chart.spacingTop = Math.max((chart.spacingTop as number | undefined) ?? 10, gap);
    return { ...config, chart };
  }
  legend.margin = Math.max((legend.margin as number | undefined) ?? 12, gap);
  return { ...config, legend };
}

const mutationRegistry: Record<string, Mutation> = {
  customRefLines: {
    scope: "widget",
    test: (wm) => (wm.customRefLines?.length ?? 0) > 0,
    apply: (config, ctx) => {
      const lines = ctx.spec.widgetMutations.customRefLines ?? [];
      const yAxis = [...((config.yAxis as Record<string, unknown>[]) ?? [{}])];

      // Compute per-axis averages for "computeFromData" lines — pooling both
      // axes' series would skew the average when left/right scales differ.
      // The average rounds to the decimal count of the series it summarizes, read
      // from the backend value_label on the points. A series without labels keeps 2.
      const dataAvgByAxis: Record<number, number | null> = {};
      const dataDecimalsByAxis: Record<number, number> = {};
      const axisDataAvg = (axisIdx: number): number | null => {
        if (axisIdx in dataAvgByAxis) return dataAvgByAxis[axisIdx];
        const allSeries = (config.series as Record<string, unknown>[]) ?? [];
        let sum = 0;
        let count = 0;
        let decimals: number | null = null;
        for (const s of allSeries) {
          if (((s.yAxis as number) ?? 0) !== axisIdx) continue;
          const pts = s.data as DataPoint[];
          if (!pts) continue;
          const seriesDecimals = inferDecimals(pts);
          if (seriesDecimals != null && (decimals == null || seriesDecimals > decimals)) decimals = seriesDecimals;
          for (const p of pts) {
            if (p == null) continue;
            if (typeof p !== "number" && p.custom?.injured === true) continue;
            const v = typeof p === "number" ? p : p.y;
            if (typeof v === "number" && !isNaN(v)) {
              sum += v;
              count++;
            }
          }
        }
        dataDecimalsByAxis[axisIdx] = decimals ?? 2;
        dataAvgByAxis[axisIdx] = count > 0 ? roundToDecimals(sum / count, dataDecimalsByAxis[axisIdx]) : null;
        return dataAvgByAxis[axisIdx];
      };

      // Group lines by target axis index (left → 0, right → 1)
      for (const l of lines) {
        const axisIdx = l.yAxis === "right" ? 1 : 0;
        const resolvedValue = l.computeFromData ? axisDataAvg(axisIdx) : l.value;
        if (resolvedValue == null) continue;

        // Extend yAxis array if needed
        while (yAxis.length <= axisIdx) yAxis.push({});
        const axis = { ...yAxis[axisIdx] };
        const existing = (axis.plotLines as unknown[]) ?? [];

        const labelText = l.computeFromData
          ? `Avg: ${formatToDecimals(resolvedValue, dataDecimalsByAxis[axisIdx] ?? 2)}`
          : l.label;
        axis.plotLines = [
          ...existing,
          {
            value: resolvedValue,
            color: l.colorHex ?? "#ff4d4f",
            width: 2,
            dashStyle: "ShortDash",
            label: labelText
              ? { text: labelText, style: { color: l.colorHex ?? "#ff4d4f" } }
              : undefined,
            zIndex: 5,
          },
        ];
        // Highcharts clips plot lines outside the axis extremes. Widen the
        // soft extremes (with 5% headroom) so the line always renders.
        const pad = Math.abs(resolvedValue) * 0.05;
        const prevSoftMax = axis.softMax as number | undefined;
        axis.softMax =
          prevSoftMax == null ? resolvedValue + pad : Math.max(prevSoftMax, resolvedValue + pad);
        const prevSoftMin = axis.softMin as number | undefined;
        axis.softMin =
          prevSoftMin == null ? resolvedValue - pad : Math.min(prevSoftMin, resolvedValue - pad);
        yAxis[axisIdx] = axis;
      }

      return { ...config, yAxis };
    },
  },

  showACWRRefLines: {
    scope: "series",
    test: (pm) => pm.showACWRRefLines === true,
    apply: (config, ctx) => {
      const yAxis = [...((config.yAxis as Record<string, unknown>[]) ?? [])];
      const targetIdx = ctx.yAxis ?? 0;
      if (targetIdx >= yAxis.length) return config;
      const axis = { ...yAxis[targetIdx] };
      const existing = (axis.plotLines as Record<string, unknown>[]) ?? [];
      // Deduplication guard.
      if (existing.some((pl) => pl.value === 1.3)) return config;
      axis.plotLines = [
        ...existing,
        {
          value: 1.3,
          color: "#cf1322",
          dashStyle: "ShortDash",
          width: 1.5,
          zIndex: 5,
          label: { text: "1.3", align: "right", style: { color: "#cf1322", fontSize: "10px" } },
        },
        {
          value: 0.8,
          color: "#237804",
          dashStyle: "ShortDash",
          width: 1.5,
          zIndex: 5,
          label: { text: "0.8", align: "right", style: { color: "#237804", fontSize: "10px" } },
        },
      ];
      // Ensure the axis range extends to include the reference lines.
      axis.softMin = 0;
      yAxis[targetIdx] = axis;
      return { ...config, yAxis };
    },
  } as SeriesMutation,

  showReferenceBands: {
    scope: "series",
    test: (pm) => pm.showReferenceBands === true,
    apply: (config, ctx) => {
      const seriesDef = ctx.spec.series.find((s) => s.queryId === ctx.queryId);
      const bands = seriesDef?.referenceBands ?? [];
      if (bands.length === 0) return config;
      // The ACWR toggle hides the parent series, so its bands draw for the companion
      // that renders in its place. A series with nothing rendered keeps its bands off.
      const companionIds = new Set(
        ctx.spec.series
          .filter(
            (s) =>
              s.series_builder_misc?.isAcwrCompanion === true &&
              s.series_builder_misc.parentQueryId === ctx.queryId,
          )
          .map((s) => s.queryId),
      );
      const hcSeries = (config.series as Record<string, unknown>[]) ?? [];
      const parentRendered = hcSeries.some((s) => s._queryId === ctx.queryId);
      const companionRendered = hcSeries.some((s) => companionIds.has(s._queryId as string));
      if (!parentRendered && !companionRendered) return config;
      const yAxis = [...((config.yAxis as Record<string, unknown>[]) ?? [])];
      const targetIdx = ctx.yAxis ?? 0;
      if (targetIdx >= yAxis.length) return config;
      const axis = { ...yAxis[targetIdx] };
      const existing = (axis.plotBands as Record<string, unknown>[]) ?? [];
      // A band label sits outside the plot area so it never covers the data:
      // right of the plot on a vertical chart, above it on a horizontal one. With
      // two y-axes both margins hold axis labels, so the band label stays inside.
      const placement = bandLabelPlacement(ctx.spec.display.inverted === true, yAxis.length > 1);
      axis.plotBands = [
        ...existing,
        ...bands.map((b) => {
          const color = b.colorHex ?? DEFAULT_REFERENCE_BAND_COLOR;
          const label =
            SHOW_REFERENCE_BAND_LABELS && b.label ? bandLabelOptions(b.label, color, placement) : undefined;
          return { from: b.lower, to: b.upper, color, zIndex: 0, ...(label && { label }) };
        }),
      ];
      // Highcharts clips a band outside the axis extremes. Widen the soft
      // extremes so every band is visible. Caution: the bands are authored in the
      // parent metric's units, and a companion's axis holds the ACWR ratio. A widen
      // to a metric-unit band would flatten the ACWR line, so a companion keeps
      // the axis extremes and lets an out-of-range band clip.
      if (parentRendered) {
        const lowest = Math.min(...bands.map((b) => b.lower));
        const highest = Math.max(...bands.map((b) => b.upper));
        const prevSoftMin = axis.softMin as number | undefined;
        axis.softMin = prevSoftMin == null ? lowest : Math.min(prevSoftMin, lowest);
        const prevSoftMax = axis.softMax as number | undefined;
        axis.softMax = prevSoftMax == null ? highest : Math.max(prevSoftMax, highest);
      }
      yAxis[targetIdx] = axis;
      if (!SHOW_REFERENCE_BAND_LABELS) return { ...config, yAxis };
      return reserveBandLabelSpace(
        { ...config, yAxis },
        placement,
        bands.map((b) => b.label ?? "").filter(Boolean),
      );
    },
  },

  showTrendline: {
    scope: "series",
    test: (pm) => pm.showTrendline === true,
    apply: (config, ctx) => {
      const series = [...((config.series as Record<string, unknown>[]) ?? [])];
      const idx = series.findIndex(
        (s) => (s as Record<string, unknown>)._queryId === ctx.queryId,
      );
      if (idx < 0) return config;
      const source = series[idx];
      const data = source.data as DataPoint[];
      const { slope, intercept } = linearRegression(data);
      const decimals = inferDecimals(data) ?? 2;
      const trendData = data.map((_, i) => roundToDecimals(slope * i + intercept, decimals));
      series.push({
        name: formatTrendlineName(source.name as string),
        type: "line",
        data: trendData,
        yAxis: (source as Record<string, unknown>).yAxis ?? (ctx.yAxis ?? 0),
        color: source.color,
        dashStyle: "ShortDash",
        lineWidth: 1,
        marker: { enabled: false },
        enableMouseTracking: false,
      });
      return { ...config, series };
    },
  },
};

/**
 * Walk all mutation flags and apply registered transforms to the raw config.
 */
export function applyMutations(
  rawConfig: Record<string, unknown>,
  spec: ChartComponentSpec,
): Record<string, unknown> {
  let config = { ...rawConfig };

  for (const mutation of Object.values(mutationRegistry)) {
    if (mutation.scope !== "widget") continue;
    if ((mutation as WidgetMutation).test(spec.widgetMutations)) {
      config = mutation.apply(config, { spec });
    }
  }

  spec.series.forEach((seriesDef) => {
    const ctx: MutationContext = {
      queryId: seriesDef.queryId,
      yAxis: Y_AXIS_INDEX[seriesDef.yAxis] ?? 0,
      spec,
    };
    for (const mutation of Object.values(mutationRegistry)) {
      if (mutation.scope !== "series") continue;
      if ((mutation as SeriesMutation).test(seriesDef.plotMutations)) {
        config = mutation.apply(config, ctx);
      }
    }
  });

  return config;
}
